import { useCallback, useEffect, useRef, useState } from 'react'

export interface RemoteData<T> {
  data: T | null
  /** True while a request is in flight, including focus refetches. */
  loading: boolean
  /** The last failure; cleared by the next success. */
  error: string | null
  reload: () => void
  /**
   * Replace the held value without a round trip (e.g. after a write the caller just made),
   * dropping any in-flight response that would otherwise land with the pre-write value.
   */
  setData: (data: T) => void
}

/**
 * Loads a remote value, refetches on window focus, and drops any response that a newer
 * request has superseded. `fetcher` identity drives reloads: wrap it in `useCallback`
 * with its inputs so a changed id refetches and a stable one does not.
 */
export function useRemoteData<T>(fetcher: () => Promise<T>): RemoteData<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Shared request identity: every load() bumps this, so any earlier in-flight
  // response (initial load or a prior focus refetch) is ignored once superseded.
  const requestId = useRef(0)

  const load = useCallback(() => {
    const id = ++requestId.current
    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const next = await fetcher()
        if (id === requestId.current) setData(next)
      } catch (err: unknown) {
        if (id === requestId.current) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (id === requestId.current) setLoading(false)
      }
    })()
    return () => {
      // Invalidate this request so a late response can't apply after cleanup.
      requestId.current++
    }
  }, [fetcher])

  useEffect(() => {
    const cancel = load()
    function onFocus() {
      load()
    }
    window.addEventListener('focus', onFocus)
    return () => {
      cancel()
      window.removeEventListener('focus', onFocus)
    }
  }, [load])

  const replaceData = useCallback((next: T) => {
    requestId.current++
    setData(next)
    setLoading(false)
    setError(null)
  }, [])

  return { data, loading, error, reload: load, setData: replaceData }
}
